import { Routes, Route, Navigate } from "react-router-dom";
import { LandingPage } from "./pages/Landing";
import { ChooseChainPage } from "./pages/ChooseChain";
import { DashboardPage } from "./pages/Dashboard";
import { AgentDetailPage } from "./pages/AgentDetail";
import { RegisterAgentPage } from "./pages/RegisterAgent";
import { ChroniclePage } from "./pages/Chronicle";
import { SettingsPage } from "./pages/Settings";
import { BrandPage } from "./pages/Brand";
import { PreviewSuiPage } from "./pages/PreviewSui";
import { PreviewShellPage } from "./pages/PreviewShell";
import { PreviewCreatePage } from "./pages/PreviewCreate";
import { PreviewDashPage } from "./pages/PreviewDash";
import { SuiApp, SuiDashboardRoute, SuiAgentsRoute, SuiCreateRoute, SuiAgentDetailRoute, SuiChronicleRoute, SuiSettingsRoute } from "./pages/SuiApp";
import { SuiDashboardPage } from "./pages/SuiDashboard";
import { useAuth } from "./components/providers/AuthProvider";
import { ConnectGate } from "./components/ConnectGate";
import type { ReactNode } from "react";

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  // Hydrating: localStorage session check in flight. Render a quiet loading
  // shell so we don't flash the ConnectGate over a perfectly valid persisted
  // session.
  if (status === "hydrating") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg)",
          color: "var(--slate-2)",
          fontFamily: "var(--font-mono)",
          fontSize: "11px",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        Restoring session…
      </div>
    );
  }
  if (status !== "ready") return <ConnectGate />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/connect" element={<ChooseChainPage />} />
      <Route path="/brand" element={<BrandPage />} />
      {import.meta.env.DEV && <Route path="/preview/sui" element={<PreviewSuiPage />} />}
      {import.meta.env.DEV && <Route path="/preview/shell" element={<PreviewShellPage />} />}
      {import.meta.env.DEV && <Route path="/preview/create" element={<PreviewCreatePage />} />}
      {import.meta.env.DEV && <Route path="/preview/dash" element={<PreviewDashPage />} />}
      {import.meta.env.DEV && <Route path="/preview/empty" element={<SuiDashboardPage agents={[]} />} />}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agents/new"
        element={
          <RequireAuth>
            <RegisterAgentPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agents/:id"
        element={
          <RequireAuth>
            <AgentDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/audit"
        element={
          <RequireAuth>
            <ChroniclePage />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route path="/sui" element={<SuiApp />}>
        <Route index element={<Navigate to="/sui/dashboard" replace />} />
        <Route path="dashboard" element={<SuiDashboardRoute />} />
        <Route path="agents" element={<SuiAgentsRoute />} />
        <Route path="agents/new" element={<SuiCreateRoute />} />
        <Route path="agents/:id" element={<SuiAgentDetailRoute />} />
        <Route path="audit" element={<SuiChronicleRoute />} />
        <Route path="settings" element={<SuiSettingsRoute />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
